import React, { useState, useEffect } from "react";
import { getAnalytics } from "../../api";
import { Eye, Users, FileText, TrendingUp, BarChart3 } from "lucide-react";

const StatCard = ({ icon: Icon, label, value, suffix }) => (
  <div className="bg-white border border-neutral-200 rounded-xl p-5">
    <div className="flex items-center justify-between">
      <p className="text-sm text-neutral-500">{label}</p>
      <Icon size={18} className="text-brand" />
    </div>
    <p className="font-heading font-extrabold text-3xl text-neutral-900 mt-2">
      {value}{suffix || ""}
    </p>
  </div>
);

const AnalyticsPanel = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
     
    getAnalytics().then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return <p className="text-neutral-500">Chargement...</p>;

  const maxDay = Math.max(1, ...data.by_day.map((d) => d.count));

  return (
    <div>
      <div className="flex items-center gap-2 mb-5">
        <BarChart3 size={20} className="text-brand" />
        <h2 className="font-heading font-bold text-xl text-neutral-900">Suivi du site web</h2>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Eye} label="Visites totales" value={data.total_visits} />
        <StatCard icon={Users} label="Visiteurs uniques" value={data.unique_visitors} />
        <StatCard icon={FileText} label="Demandes de devis" value={data.quotes} />
        <StatCard icon={TrendingUp} label="Taux de conversion" value={data.conversion_rate} suffix="%" />
      </div>

      <div className="grid lg:grid-cols-2 gap-5 mt-6">
        <div className="bg-white border border-neutral-200 rounded-xl p-5">
          <h3 className="font-semibold text-neutral-800 mb-4">Visites (14 derniers jours)</h3>
          {data.by_day.length === 0 ? (
            <p className="text-sm text-neutral-400">Aucune donnée pour le moment.</p>
          ) : (
            <div className="flex items-end gap-1.5 h-40">
              {data.by_day.map((d) => (
                <div key={d.date} className="flex-1 flex flex-col items-center gap-1 group">
                  <div className="w-full bg-brand/80 hover:bg-brand rounded-t transition-all relative"
                       style={{ height: `${(d.count / maxDay) * 100}%`, minHeight: "4px" }}>
                    <span className="absolute -top-5 left-1/2 -translate-x-1/2 text-[10px] text-neutral-500 opacity-0 group-hover:opacity-100">{d.count}</span>
                  </div>
                  <span className="text-[9px] text-neutral-400 rotate-45 origin-left whitespace-nowrap">{d.date.slice(5)}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white border border-neutral-200 rounded-xl p-5">
          <h3 className="font-semibold text-neutral-800 mb-4">Pages les plus vues</h3>
          {data.top_pages.length === 0 ? (
            <p className="text-sm text-neutral-400">Aucune donnée.</p>
          ) : (
            <div className="space-y-2">
              {data.top_pages.map((p) => (
                <div key={p.path} className="flex items-center justify-between text-sm">
                  <span className="text-neutral-700 truncate">{p.path || "/"}</span>
                  <span className="font-semibold text-neutral-900 ml-3">{p.count}</span>
                </div>
              ))}
            </div>
          )}
          <h3 className="font-semibold text-neutral-800 mt-6 mb-3">Sources de trafic</h3>
          <div className="space-y-2">
            {data.top_referrers.map((r) => (
              <div key={r.referrer} className="flex items-center justify-between text-sm">
                <span className="text-neutral-700 truncate">{r.referrer || "direct"}</span>
                <span className="font-semibold text-neutral-900 ml-3">{r.count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsPanel;
